import { action } from '@ember/object';
import EmberService, { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import moment from 'moment';

import type { Service } from 'webapp-types';
import type { BillingAddonType } from 'webapp/enums/enums';
import { BillingCycle, PlanExtrasType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { getFormattedPrice } from 'webapp/utils/number';

import type BillingPlan from 'webapp/models-custom/billing-plan';
import { ITEM_ID_EXTRA_DS } from 'webapp/models-custom/subscription';

// Annual Plan Flow (Experiment 320223)
// https://app.asana.com/0/0/1202159607187245/f

// Flow type is according to signup type (signups from template or regular signup)
export enum FlowType {
	TEMPLATE = '320223_A',
	REGULAR = '320223_B',
}

export default class FlowAnnualPlanService extends EmberService {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	get trackingOrigin(): string {
		return this.isEligibleForRegularFlow ? FlowType.REGULAR : FlowType.TEMPLATE;
	}

	get isEligibleForRegularFlow(): boolean {
		return !this.currentUserService.isSignedUpFromTemplate; // regular signup
	}

	get currentPlan(): BillingPlan | null {
		return this.billingService.activePlan;
	}

	get currentPlanType() {
		return this.currentPlan?.planType;
	}

	get isEligibleToShowMonthlyNotification(): boolean {
		return (
			this.isEligibleForRegularFlow &&
			this.billingService.isTrial &&
			this.currentPlan?.billingCycle === BillingCycle.YEARLY
		);
	}

	get isEligibleToShowYearlyNotification(): boolean {
		const { isActive, isTrial } = this.billingService;

		return (
			this.isEligibleForRegularFlow &&
			(isActive || isTrial) &&
			!this.currentPlan?.isFree &&
			this.currentPlan?.billingCycle !== BillingCycle.YEARLY &&
			this.billingService.yearlySavings > 0
		);
	}

	get yearlyToMonthlySavingsAmount(): string {
		const currentPlanTotal = this.billingService.upcomingInvoiceData?.total || 0,
			currentSubscription = this.billingService.subscription;
		let savings = 0;

		if (this.currentPlan?.isYearly) {
			const monthlyPlan = this.billingService.availablePlans
				.filter((item) => item.planType === this.currentPlanType)
				.find((item) => item.isMonthly);

			if (monthlyPlan) {
				let currentPlanMonthlyTotal = monthlyPlan.price;

				currentSubscription.items.forEach((item) => {
					if (item.id === ITEM_ID_EXTRA_DS) {
						currentPlanMonthlyTotal += monthlyPlan.sourcePrice * item.quantity;
					}

					// Add-ons only for paid subscriptions
					if (item.type === PlanExtrasType.ADD_ON) {
						const addonPrice =
							monthlyPlan.getAddonPrice(item.id as BillingAddonType) || 0;

						currentPlanMonthlyTotal += addonPrice * item.quantity;
					}
				});

				savings = currentPlanMonthlyTotal * 12 - currentPlanTotal;
			}
		}

		return getFormattedPrice(savings);
	}

	// Top bar
	@cached
	get shouldYearlyUpgradeNotificationBeShownThisMonth(): boolean {
		const { activeUntil: activeUntilTs = 0, becamePaying: becamePayingTs = 0 } =
				this.billingService,
			currentTs = moment().unix();

		if (becamePayingTs && activeUntilTs) {
			const payingForTwoMonths = moment.unix(becamePayingTs).add(2, 'months').unix(),
				fiveDaysBeforeActiveUntilTs = moment.unix(activeUntilTs).subtract(5, 'days').unix();

			if (
				payingForTwoMonths <= currentTs &&
				fiveDaysBeforeActiveUntilTs <= currentTs &&
				fiveDaysBeforeActiveUntilTs >
					this.appNotificationsService.skipYearlyUpgradeNotification
			) {
				return true;
			}
		}

		return false;
	}

	get isEligibleToShowYearlyUpgradeNotification(): boolean {
		return (
			this.isEligibleForRegularFlow &&
			this.currentUserService.isAdmin &&
			!!this.currentPlan &&
			!this.currentPlan.isVIP &&
			!this.currentPlan.isFree &&
			this.currentPlan.billingCycle !== BillingCycle.YEARLY &&
			!this.billingService.isTrial &&
			this.shouldYearlyUpgradeNotificationBeShownThisMonth &&
			this.billingService.yearlySavings > 0
		);
	}

	@action
	triggerSkipYearlyUpgradeNotification(): void {
		void this.userSettingsService
			.save(UserSettingsKey.SKIP_YEARLY_UPGRADE_NOTIFICATION, moment().unix())
			.then(() => {
				this.analyticsService.event(TrackingEvent.ANNUAL_BANNER_SKIPPED, {
					experiments: [FlowType.REGULAR],
				});
			});
	}
}

declare module '@ember/service' {
	interface Registry {
		'flow/annual-plan': FlowAnnualPlanService;
	}
}
